import {
  Box,
  Divider,
  Grid2 as Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { isEmpty, isNil } from "lodash";
import { useEffect, useState } from "react";
import {
  Control,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  Body,
  Header,
  Title,
} from "../styles/OrderingPhysician";
import {
  OrderingPhysicianDetails,
  PrimaryPhysicianDetails,
} from "../../../models/PhysicianDetails";
import {
  fieldNames,
  fieldsMap,
  NppesType,
  physicianNames,
  primaryPhysicianFormNames,
} from "../../../constants/PhysicianDetails";
import { defaultPrimaryPhysician, TabData } from "../../../models/TabSessionStorage";
import {
  getFormattedPhoneNo,
  getFormValues,
  getValue,
  isEmptyValue,
} from "../../../utils";
import PhysicianInputForm from "../containers/PhysicianInputForm";
import { TextConstants } from "../../../constants/Patient";
import { TypographyBox } from "../../../app/common/TextLabel";
import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";
import { Numbers } from "../../../constants/Referral";
import { updateTabStatus } from "../../../constants/TabSessionStorage";

export interface PropsFromState {
  getValues: UseFormGetValues<TabData>;
  control: Control<any>;
  clearErrors: UseFormClearErrors<TabData>;
  watch: UseFormWatch<TabData>;
  reset: UseFormReset<TabData>;
  setValue: UseFormSetValue<TabData>;
  tabSessionStorage: TabSessionStorageResponse;
}

type AllProps = PropsFromState;

let debounceTimeout: NodeJS.Timeout | undefined;

const OrderingPhysician: React.FC<AllProps> = ({
  getValues,
  setValue,
  reset,
  watch,
  control,
  tabSessionStorage
}: AllProps) => {
  const [orderingPhysician, setOrderingPhysician] =
    useState<OrderingPhysicianDetails>();
  const [primaryPhysician, setPrimaryPhysician] =
    useState<PrimaryPhysicianDetails>();

  const [
    openPrimaryPhysicianAutocomplete,
    setOpenPrimaryPhysicianAutocomplete,
  ] = useState(false);
  const [openOrderingPhysician, setOpenOrderingPhysician] = useState(false);
  const [openPrimaryPhysician, setOpenPrimaryPhysician] = useState(false);
  const [
    openOrderingPhysicianAutocomplete,
    setOpenOrderingPhysicianAutocomplete,
  ] = useState(false);
  const [toggleState, setToggleState] = useState(getValues().physicianDetails.orderingPhysician.physicianSameAsPrimary || false);
  const [noValidate, setNoValidate] = useState<boolean>(true);

  useEffect(() => {
    setToggleState(
      getValues("physicianDetails.orderingPhysician.physicianSameAsPrimary") || false
    );
  }, []);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (
        value &&
        name && name.startsWith("physicianDetails.primaryPhysician.")
      ) {
        if (!isEmptyValue(getFormValues(fieldNames, getValues))) {
          setNoValidate(false);
        } else {
          setNoValidate(true);
        }
      }
      if (
        value &&
        name &&
        getValue(value, "physicianDetails.primaryPhysician.physicianName")
      ) {
        if (
            name.startsWith("physicianDetails.primaryPhysician.") ||
            name === "physicianDetails.orderingPhysician.physicianSameAsPrimary"
        ) {
          if (debounceTimeout) clearTimeout(debounceTimeout);
          debounceTimeout = setTimeout(() => {
            handlePrimaryPhysicianState(name, value, setValue, toggleState);
          }, 300);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, toggleState]);

  const handlePrimaryPhysicianState = (
    name: string | undefined,
    value: any,
    setValue: UseFormSetValue<TabData>,
    isPrimaryPhysician: boolean
  ) => {

    for (const { key } of fieldsMap) {
      const primaryField = `physicianDetails.primaryPhysician.${key}`;
      if (
      propertyCheck(
        isPrimaryPhysician,
        name,
        primaryField,
        value.physicianDetails.primaryPhysician[key]
      )
      ) {
      let orderingValue = value.physicianDetails.primaryPhysician[key];
      if (key === "latitude" || key === "longitude") {
        orderingValue = getValue(value, primaryField, "");
      } else {
        orderingValue = orderingValue ? orderingValue : "";
      }
      setValue(
        `physicianDetails.orderingPhysician.${key}` as keyof TabData,
        orderingValue
      );
      break;
      }
    }

    if (isPrimaryPhysician) {
      setSameAsPrimaryPhysician();
    }
  };

  const propertyCheck = (
    isOrdering: boolean,
    name: string | undefined,
    nameString: string,
    value: any
  ) => {
    return isOrdering && name === nameString && !isNil(value);
  };

  const choosePrimaryPhysician = (option: any) => {
    setPrimaryPhysician(option);
    setNoValidate(false);
    if (!isNil(option)) {
      const {
        name,
        npiId,
        phoneNo,
        phoneExt,
        county,
        streetName1,
        streetName2,
        fax,
        city,
        state,
        zipCode,
      } = option;
      reset({
        ...getValues(),
        physicianDetails: {
          ...getValues("physicianDetails"),
          primaryPhysician: {
            ...getValues("physicianDetails.primaryPhysician"),
            physicianName: name,
            physicianNpiId: npiId,
            streetName1: streetName1,
            streetName2: streetName2,
            city: city,
            county: county,
            physicianState: state,
            zipCode: zipCode,
            fax: getFormattedPhoneNo(fax),
            phoneExt: phoneExt,
            phoneNo: phoneNo ? getFormattedPhoneNo(phoneNo) : "",
            physicianTypeId: 1,
            sameAsOrdering: false,
          },
        },
      });

       if (getValues("physicianDetails.orderingPhysician.physicianSameAsPrimary")) {
        setSameAsPrimaryPhysician();
      }
    }
  };

  const chooseOrderingPhysician = (option: any) => {
    setOrderingPhysician(option);
    if (!isNil(option)) {
      const {
        name,
        npiId,
        phoneNo,
        phoneExt,
        county,
        streetName1,
        streetName2,
        fax,
        city,
        state,
        zipCode,
      } = option;
      reset({
        ...getValues(),
        physicianDetails: {
          ...getValues("physicianDetails"),
          orderingPhysician: {
            ...getValues("physicianDetails.orderingPhysician"),
            physicianName: name,
            physicianNpiId: npiId,
            streetName1: streetName1,
            streetName2: streetName2,
            city: city,
            county: county,
            zipCode: zipCode,
            fax: getFormattedPhoneNo(fax),
            physicianState: state,
            phoneExt: phoneExt,
            phoneNo: phoneNo ? getFormattedPhoneNo(phoneNo) : "",
            physicianTypeId: 2,
          },
          ...(toggleState && {
            primaryPhysician: {
              ...getValues("physicianDetails.orderingPhysician"),
              physicianTypeId: 1,
            },
          }),
        },
      });
    }
  };

  const orderingPhysicianFormNames = {
    physicianName: "physicianDetails.orderingPhysician.physicianName",
    physicianNpiId: "physicianDetails.orderingPhysician.physicianNpiId",
    phoneNo: "physicianDetails.orderingPhysician.phoneNo",
    phoneExt: "physicianDetails.orderingPhysician.phoneExt",
    taxId: "physicianDetails.orderingPhysician.taxId",
    address: [
      "physicianDetails.orderingPhysician.streetName1",
      "physicianDetails.orderingPhysician.streetName2",
      "physicianDetails.orderingPhysician.city",
      "physicianDetails.orderingPhysician.county",
      "physicianDetails.orderingPhysician.physicianState",
      "physicianDetails.orderingPhysician.zipCode",
      "physicianDetails.orderingPhysician.latitude",
      "physicianDetails.orderingPhysician.longitude",
    ],
    fax: "physicianDetails.orderingPhysician.fax",
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
   if (isChecked) {
      setToggleState(isChecked);
     setSameAsPrimaryPhysician();
    } else {
      clearOrderingPhysicianDetails();
    }
  };

  const setSameAsPrimaryPhysician = () => {
    reset({
      ...getValues(),
      physicianDetails: {
        ...getValues("physicianDetails"),
        orderingPhysician: {
          ...getValues("physicianDetails.primaryPhysician"),
          physicianTypeId: 2,
          physicianSameAsPrimary: true,
        },
        primaryPhysician: {
          ...getValues("physicianDetails.primaryPhysician"),
          physicianTypeId: 1,
        },
      },
    });
  };

  const clearPrimaryPhysicianDetails = () => {
    setToggleState(false);
    reset({
      ...getValues(),
      physicianDetails: {
        ...getValues("physicianDetails"),
        primaryPhysician: defaultPrimaryPhysician,
      },
    });
    setNoValidate(true);
  };

  const clearOrderingPhysicianDetails = () => {
    if (toggleState) {
      setToggleState(false);
    }
    reset({
      ...getValues(),
      physicianDetails: {
        ...getValues("physicianDetails"),
        orderingPhysician: {
          physicianName: "",
          physicianNpiId: "",
          streetName1: "",
          streetName2: "",
          city: "",
          county: "",
          physicianState: "",
          zipCode: "",
          fax: "",
          phoneExt: "",
          phoneNo: "",
          physicianTypeId: 2,
          longitude: null,
          latitude: null,
        },
        primaryPhysician: {
          ...getValues("physicianDetails.primaryPhysician"),
          sameAsOrdering: false,
        },
      },
    });
  };

  useEffect(() => {
    if (
      tabSessionStorage &&
      isEmpty(getValue(tabSessionStorage, "response.physicianDetails.orderingPhysician.physicianName")) &&
      Number(getValue(tabSessionStorage, "response.referralSource.facilityTypeId")) === Numbers.Four
    ) {
      reset({
        ...getValues(),
        physicianDetails: {
          ...getValues("physicianDetails"),
          orderingPhysician: {
            physicianName: getValue(tabSessionStorage, "response.referralSource.facilityName"),
            physicianNpiId: getValue(tabSessionStorage, "response.referralSource.referralSourceNpi"),
            streetName1: getValue(tabSessionStorage, "response.referralSource.streetName1"),
            streetName2: getValue(tabSessionStorage, "response.referralSource.streetName2"),
            city: getValue(tabSessionStorage, "response.referralSource.city"),
            county: getValue(tabSessionStorage, "response.referralSource.county"),
            physicianState: getValue(tabSessionStorage, "response.referralSource.referralSourceState"),
            zipCode: getValue(tabSessionStorage, "response.referralSource.zipCode"),
            fax: getValue(tabSessionStorage, "response.referralSource.referralSourceFax"),
            phoneExt: getValue(tabSessionStorage, "response.referralSource.referralSourceExtension"),
            phoneNo: getValue(tabSessionStorage, "response.referralSource.referralSourcePhoneNo"),
            physicianTypeId: 2,
            longitude: null,
            latitude: null,
          },
        },
      });
    }
  }, [tabSessionStorage]);

  useEffect(() => {
    setTimeout(() => {
      if (noValidate) {
        reset(
          {
            ...getValues(),
          },
          { keepErrors: false }
        );
      }
    }, 10);
  }, [noValidate]);
  useEffect(() => {
    reset({
      ...getValues(),
      tabStatus: updateTabStatus(2, getValues("tabStatus")),
    });
  }, []);

  return (
    <>
      <Grid size={{ xs: 12 }} pt={"0.5rem"} sx={{ marginX: 2 }}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Box pb={2}>
              <Typography variant="subtitle1" fontWeight={fontWeight[5]}>
                {TextConstants.PHYSICIAN_VERBIAGE}
              </Typography>
            </Box>
            <Grid container sx={Header}>
              <Grid size={{ xs: 6 }}>
                <Box sx={Title}>
                  {TypographyBox(
                    "Primary Care Physician",
                    colors.fonts[20],
                    fontWeight[5]
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={Body} data-testid="primary-physician">
            <PhysicianInputForm
              control={control}
              getValues={getValues}
              setValue={setValue}
              reset={reset}
              physician={primaryPhysician || null}
              names={primaryPhysicianFormNames}
              choosePhysician={choosePrimaryPhysician}
              open={openPrimaryPhysician}
              setOpen={setOpenPrimaryPhysician}
              setOpenAutocomplete={setOpenPrimaryPhysicianAutocomplete}
              openAutocomplete={openPrimaryPhysicianAutocomplete}
              physicianFormName={physicianNames.primary}
              noValidate={noValidate}
              renderClearButton={clearPrimaryPhysicianDetails}
              type={NppesType.PhysicianOffice}
            />
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ marginX: 2 }} />
      <Grid size={{ xs: 12 }} pt={"1rem"} sx={{ marginX: 2 }}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={Header}>
              <Grid size={{ xs: 6 }}>
                <Box sx={Title} display={"flex"}>
                  {TypographyBox(
                    `Ordering Physician`,
                    colors.fonts[20],
                    fontWeight[5]
                  )}
                  <Stack direction="row" spacing={3} pl={2} alignItems="center">
                    <Switch
                      inputProps={{ "aria-label": "ant design" }}
                      onChange={handleToggle}
                      name={"physicianDetails.orderingPhysician.physicianSameAsPrimary"}
                      checked={toggleState}
                      sx={{ marginRight: "-0.8rem" }}
                    />
                    {TypographyBox(
                      "Same as primary physician",
                      colors.fonts[4],
                      fontWeight[6]
                    )}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={Body} data-testid="ordering-physician">
            <PhysicianInputForm
              control={control}
              getValues={getValues}
              setValue={setValue}
              reset={reset}
              physician={orderingPhysician || null}
              names={orderingPhysicianFormNames}
              choosePhysician={chooseOrderingPhysician}
              open={openOrderingPhysician}
              setOpen={setOpenOrderingPhysician}
              openAutocomplete={openOrderingPhysicianAutocomplete}
              setOpenAutocomplete={setOpenOrderingPhysicianAutocomplete}
              physicianFormName={physicianNames.ordering}
              noValidate={false}
              isReadOnly={toggleState}
              renderClearButton={clearOrderingPhysicianDetails}
              type={NppesType.PhysicianOffice}
            />
          </Grid>
        </Grid>
        <Divider />
      </Grid>
    </>
  );
};

export default OrderingPhysician;